import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FASTAPI_HOST } from '../settings/config';
import {
  REFRESH_EARLY_MS, appTokenExpiresAt, clearAppToken, saveAppToken,
} from '../auth/appSession';

/** Do not render authenticated features until Clerk and the app JWT agree. */
export default function AppAuthGate({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const syncInFlight = useRef(false);

  const syncAppToken = useCallback(async () => {
    if (!isSignedIn || !user) return;
    const existingExpiry = appTokenExpiresAt();
    if (existingExpiry > Date.now() + REFRESH_EARLY_MS) {
      setReady(true);
      setError('');
      return;
    }
    // Effects, timers, and user retries may overlap. Only one Clerk-to-app
    // token exchange may run for this gate at a time.
    if (syncInFlight.current) return;
    syncInFlight.current = true;

    const existingTokenStillValid = existingExpiry > Date.now();
    // Keep mounted conversations usable while renewing shortly before expiry.
    // Only block rendering when there is no currently valid app session.
    if (!existingTokenStillValid) {
      setReady(false);
      clearAppToken();
    }
    try {
      // The backend is the authority on expiry. Force Clerk to mint a fresh
      // short-lived session token instead of reusing an in-memory cached JWT.
      const clerkToken = await getToken({ skipCache: true });
      if (!clerkToken) throw new Error('Your sign-in session could not be restored.');
      const response = await axios.post(
        `${FASTAPI_HOST}/api/login_external`,
        {
          username: user.username || '',
          full_name: user.fullName || '',
          password: '',
          email: user.primaryEmailAddress?.emailAddress || '',
          external_auth_provider: 'clerk',
          external_user_id: user.id,
        },
        { headers: { Authorization: `Bearer ${clerkToken}` } },
      );
      const appToken = response.data?.access_token;
      if (!appToken) throw new Error('The server did not return an app session.');
      saveAppToken(appToken);
      setError('');
      setReady(true);
    } catch (syncError) {
      if (existingTokenStillValid) {
        // The interval will retry; a temporary refresh outage should not tear
        // down an active conversation whose current token still works.
        setReady(true);
      } else {
        clearAppToken();
        setReady(false);
        setError(
          syncError.response?.data?.detail
          || syncError.message
          || 'Your app session could not be restored.',
        );
      }
    } finally {
      syncInFlight.current = false;
    }
  }, [getToken, isSignedIn, user]);

  useEffect(() => {
    if (!isLoaded || !isUserLoaded) return undefined;
    if (!isSignedIn || !user) {
      clearAppToken();
      setReady(false);
      return undefined;
    }

    syncAppToken();
    const timer = window.setInterval(syncAppToken, 60 * 1000);
    return () => window.clearInterval(timer);
  }, [attempt, isLoaded, isSignedIn, isUserLoaded, syncAppToken, user]);

  if (isLoaded && isUserLoaded && !isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h6">We couldn’t restore your app session.</Typography>
          <Typography color="text.secondary">{error}</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => { setError(''); setAttempt((value) => value + 1); }}>
              Try again
            </Button>
            <Button variant="outlined" onClick={() => signOut()}>
              Sign out
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (!isLoaded || !isUserLoaded || !ready) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Restoring your session…</Typography>
        </Stack>
      </Box>
    );
  }

  return children;
}
